import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useCancelErase } from '../api/hooks/useAccountDeletion'

// Persistent banner shown to active users who have scheduled their
// account for 30-day hard-erase but haven't been tombstoned yet. Rides
// on authStore.user.eraseRequestedAt (populated from /auth/validate +
// authResponse).
//
// Once the user is tombstoned the banner stops mattering — they can't
// log in anymore. So this only renders during the active-grace window.
//
// Click "Cancel deletion" → POST /api/account/cancel-erase → refetch
// /me on the next interaction (handled by onSuccess invalidation in
// useCancelErase). We optimistically update the authStore here so the
// banner disappears immediately.

function daysFromNow(iso: string): number {
  const requested = new Date(iso).getTime()
  const grace = requested + 30 * 24 * 60 * 60 * 1000
  const remaining = Math.max(0, grace - Date.now())
  return Math.ceil(remaining / (24 * 60 * 60 * 1000))
}

export function ErasePendingBanner() {
  const user = useAuthStore((s) => s.user)
  const updateUser = useAuthStore((s) => s.updateUser)
  const { mutate: cancel, isPending } = useCancelErase()
  const [error, setError] = useState<string | null>(null)

  if (!user?.eraseRequestedAt) return null

  const remaining = daysFromNow(user.eraseRequestedAt)

  const handleCancel = () => {
    if (!confirm('Cancel the scheduled deletion of your account?')) return
    setError(null)
    cancel(undefined, {
      onSuccess: () => {
        updateUser({ eraseRequestedAt: null })
        try {
          const stored = localStorage.getItem('user')
          if (stored) {
            localStorage.setItem('user', JSON.stringify({ ...JSON.parse(stored), eraseRequestedAt: null }))
          }
        } catch {}
      },
      onError: () => setError('Failed to cancel. Please try again.'),
    })
  }

  return (
    <div className="bg-danger/15 border-b border-danger/40 px-4 py-2 flex items-center gap-3" role="alert">
      <AlertTriangle size={18} className="text-danger shrink-0" />
      <div className="flex-1 text-sm text-navy">
        <span className="font-semibold">Your account is scheduled for deletion in {remaining} day{remaining === 1 ? '' : 's'}.</span>
        {error && <span className="ml-2 text-danger">{error}</span>}
      </div>
      <button
        onClick={handleCancel}
        disabled={isPending}
        className="text-sm font-semibold text-navy bg-white border border-border rounded-md px-3 py-1 disabled:opacity-50"
      >
        {isPending ? 'Cancelling…' : 'Cancel deletion'}
      </button>
    </div>
  )
}
